import { useState, type CSSProperties, type KeyboardEvent } from 'react';

import { formatNumber, parseNumber, type NumberFormat } from '@/utils/number';

import { onActivateKey } from './activate';
import { cx } from './classNames';
import { FieldLabel } from './FieldLabel';

/** `d` entspricht dem .NET-Format für ganze Zahlen ohne Tausendertrennung. */
export type NumericFormat = NumberFormat | 'd';

/** Pendant zu `SfNumericTextBox`; die Spin-Schaltflächen sind standardmäßig aus. */
export interface NumericTextBoxProps {
  value: number | null;
  onChange: (value: number | null) => void;
  /** Standard `N2`. */
  format?: NumericFormat;
  min?: number;
  max?: number;
  step?: number;
  showSpinButton?: boolean;
  /** Feste Beschriftung über dem Feld (REQ-24 §2). */
  label?: string;
  /** Zusatz in schwächerer Schrift hinter der Beschriftung. */
  hint?: string;
  /** Nur für echte Beispielwerte — nicht als Ersatz für `label` (REQ-24 §3.2). */
  placeholder?: string;
  readOnly?: boolean;
  disabled?: boolean;
  id?: string;
  className?: string;
  style?: CSSProperties;
  title?: string;
}

/** Anzeige im Ruhezustand: deutsche Trennzeichen; `d` bleibt ohne Trennzeichen. */
function display(value: number | null, format: NumericFormat): string {
  if (value === null) return '';
  return format === 'd' ? String(Math.trunc(value)) : formatNumber(value, format);
}

/** Bearbeitung im Rohformat: Dezimalkomma, keine Tausenderpunkte. */
function raw(value: number | null): string {
  return value === null ? '' : String(value).replace('.', ',');
}

export function NumericTextBox(props: NumericTextBoxProps) {
  const { onChange } = props;
  const format = props.format ?? 'N2';
  const [focused, setFocused] = useState(false);
  const [draft, setDraft] = useState<string | null>(null);

  const text = draft ?? (focused ? raw(props.value) : display(props.value, format));

  const clamp = (value: number): number => {
    let result = format === 'd' ? Math.trunc(value) : value;
    if (props.min !== undefined) result = Math.max(props.min, result);
    if (props.max !== undefined) result = Math.min(props.max, result);
    return result;
  };

  const commit = (input: string) => {
    setDraft(null);
    const parsed = parseNumber(input);
    const next = parsed === null ? null : clamp(parsed);
    if (next !== props.value) onChange(next);
  };

  const spin = (direction: 1 | -1) => {
    const step = props.step ?? 1;
    onChange(clamp((props.value ?? 0) + direction * step));
  };

  const onKeyDown = (event: KeyboardEvent<HTMLInputElement>) => {
    if (event.key === 'Enter') commit(text);
    if (event.key === 'Escape') setDraft(null);
  };

  return (
    <FieldLabel
      id={props.id}
      className={props.className}
      style={props.style}
      title={props.title}
      groupClass="e-numeric"
      label={props.label}
      hint={props.hint}
      focused={focused}
      disabled={props.disabled}
    >
      {({ id }) => (
        <>
          <input
            id={id}
            type="text"
            inputMode="decimal"
            className="e-input"
            value={text}
            placeholder={props.placeholder}
            readOnly={props.readOnly === true}
            disabled={props.disabled === true}
            onChange={(event) => {
              setDraft(event.target.value);
            }}
            onFocus={() => {
              setFocused(true);
            }}
            onBlur={() => {
              setFocused(false);
              commit(text);
            }}
            onKeyDown={onKeyDown}
          />
          {props.showSpinButton === true ? (
            <>
              <span
                role="button"
                tabIndex={0}
                aria-label="Increment"
                className={cx('e-input-group-icon', 'e-spin-up', 'e-icons', 'fa-solid fa-angle-up')}
                onClick={() => {
                  spin(1);
                }}
                onKeyDown={onActivateKey(() => {
                  spin(1);
                })}
              />
              <span
                role="button"
                tabIndex={0}
                aria-label="Decrement"
                className={cx(
                  'e-input-group-icon',
                  'e-spin-down',
                  'e-icons',
                  'fa-solid fa-angle-down',
                )}
                onClick={() => {
                  spin(-1);
                }}
                onKeyDown={onActivateKey(() => {
                  spin(-1);
                })}
              />
            </>
          ) : null}
        </>
      )}
    </FieldLabel>
  );
}

export default NumericTextBox;
